import React from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { Flex, Text } from "styles/utility-components";

import Alert from "components/ui/alert/Alert";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import AttributeDisplay from "./AttributeDisplay";
import { CpvCodesContainer } from "./CpvCodes";
import OrganisationsContainer from "./OrganisationsPopover";

type ContractsProps = {
  contracts: NoticeDetails["contracts"];
};

type ContractDetailsProps = {
  contract: NoticeDetails["contracts"][0];
};

function ContractDetails({ contract }: ContractDetailsProps) {
  return (
    <Container gap="16px" column>
      <Flex gap="8px" column>
        <Flex gap="4px" column>
          <Text description color="sysTextPlaceholder">
            Contract
          </Text>
          <Text h3>
            {contract.title ??
              (contract.awardId ? `Contract ${contract.awardId}` : "Unknown Contract Title")}
          </Text>
        </Flex>
      </Flex>
      {contract.cpvCodes.length !== 0 && <CpvCodesContainer cpvCodes={contract.cpvCodes} />}
      {contract.suppliers.length !== 0 && (
        <AttributeDisplay
          attribute="Suppliers"
          customValue={<OrganisationsContainer orgs={contract.suppliers} orgType="suppliers" />}
        />
      )}
      {contract.lots.length !== 0 && (
        <AttributeDisplay attribute="Lots" customValue={<LotsDisplay lots={contract.lots} />} />
      )}
      <AttributeDisplay attribute="Value" money={contract.value} />
      <AttributeDisplay attribute="Start" date={contract.contractPeriod?.startDate} />
      <AttributeDisplay attribute="End" date={contract.contractPeriod?.endDate} />
      {contract.contractPeriod?.maxExtentDate && contract.contractPeriod?.endDate && (
        <Alert type="warning">
          Option for extension up to{" "}
          {DateTime.fromISO(contract.contractPeriod.maxExtentDate)
            .diff(DateTime.fromISO(contract.contractPeriod.endDate))
            .toFormat("M")}{" "}
          months.
        </Alert>
      )}
    </Container>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  paddingLeft: "24px",
  borderLeft: `2px solid ${theme.colors.sysBorderDecorative}`,
}));

const LotsDisplay = ({ lots }: { lots: NoticeDetails["contracts"][0]["lots"] }) => {
  if (lots.length === 0) {
    return <Text>--</Text>;
  }
  if (lots.length === 1) {
    return <Text>{lots[0].title}</Text>;
  }
  return (
    <TooltipComponent
      content={
        <PopoverContent>
          {lots.map((lot) => (
            <Text key={lot.id}>{lot.title}</Text>
          ))}
        </PopoverContent>
      }
    >
      <Text inline color="sysPrimaryDefault">
        {lots.length} Lots
      </Text>
    </TooltipComponent>
  );
};

const PopoverContent = styled.div({
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  maxHeight: "240px",
  gap: "8px",
});

export default function Contracts({ contracts }: ContractsProps) {
  return (
    <Flex column gap="24px">
      <Text h2>Contracts</Text>
      {!contracts.length ? (
        <Text>No contracts associated with this notice</Text>
      ) : (
        contracts.map((contract) => <ContractDetails key={contract.awardId} contract={contract} />)
      )}
    </Flex>
  );
}
